import React, { useState } from 'react';
import Card from './Card';

interface VerticalImageCardProps {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description?: string;
  /** 카드 아래쪽 왼쪽에 붙는 보조 정보 (예: 등록 개수) */
  meta?: React.ReactNode;
  /** 카드 아래쪽 오른쪽 안내 문구 */
  actionLabel?: string;
  onClick?: () => void;
  /** 'cover'는 이미지를 꽉 채우고, 'contain'은 여백을 두고 가운데 놓는다 */
  imageFit?: 'cover' | 'contain';
  imageBackground?: string;
  compact?: boolean;
}

/** 위에 이미지, 아래에 제목·설명이 있는 세로형 카드. 공용 Card 위에 만든다. */
const VerticalImageCard: React.FC<VerticalImageCardProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  meta,
  actionLabel,
  onClick,
  imageFit = 'contain',
  imageBackground = 'var(--Color-background-sub, #F8F8F8)',
  compact = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const isInteractive = Boolean(onClick);
  const isRaised = isInteractive && (isHovered || isFocused);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `${title}${description ? `, ${description}` : ''}` : undefined}
      onClick={onClick ? () => onClick() : undefined}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      width="100%"
      padding="0"
      gap="0"
      alignItems="stretch"
      borderRadius={compact ? '16px' : '20px'}
      boxShadow={
        isRaised
          ? '0 12px 24px -8px var(--color-alpha-shadow2, rgba(0, 0, 0, 0.14)), 0 0 0 1px var(--Color-fill-brand-color, #FF6F0F)'
          : '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 0 1px var(--Color-line-normal, #F0F0F0)'
      }
      style={{
        overflow: 'hidden',
        cursor: isInteractive ? 'pointer' : 'default',
        outline: 'none',
        transform: isRaised ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        fontFamily: 'pretendard',
        textAlign: 'left',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          background: imageBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {!imageFailed && (
          <img
            src={imageUrl}
            alt={imageAlt}
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImageFailed(true)}
            style={{
              width: imageFit === 'cover' ? '100%' : '78%',
              height: imageFit === 'cover' ? '100%' : '78%',
              objectFit: imageFit,
              transform: isRaised ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              userSelect: 'none',
            }}
          />
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? '4px' : '6px',
          padding: compact ? '12px 14px 14px' : '16px 20px 18px',
          flex: 1,
        }}
      >
        <span
          style={{
            color: 'var(--Color-text-strong, #0D0D0D)',
            fontSize: compact ? '16px' : '18px',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          {title}
        </span>
        {description && (
          <span
            style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: compact ? '12px' : '13px',
              fontWeight: 400,
              lineHeight: '150%',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: compact ? '36px' : '39px',
            }}
          >
            {description}
          </span>
        )}
        {(meta || actionLabel) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              marginTop: compact ? '6px' : '10px',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>{meta}</span>
            {actionLabel && (
              <span
                style={{
                  color: isRaised
                    ? 'var(--Color-fill-brand-color, #FF6F0F)'
                    : 'var(--Color-text-assistive, #989898)',
                  fontSize: compact ? '12px' : '13px',
                  fontWeight: 600,
                  lineHeight: '150%',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s ease',
                }}
              >
                {actionLabel} →
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default VerticalImageCard;
